import { ActionIcon, Button, Flex, Group, NumberFormatter, Progress, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import StockfishInfoModal from "./StockfishInfoModal";
import { IconArrowLeft, IconCheck, IconCopy, IconSearch, IconTargetArrow } from "@tabler/icons-react";
import { Chess } from "chess.js";
import { useState } from "react";
import { fetchLichessLink } from "./fetchLichessLink";
import HistoryTable from "./HistoryTable";
import cn from "classnames";
import { Level, Mode, Position, Settings, ShouldReset, State, User } from "./models";
import { useMistakesStorage, useStorage } from "./ZustandState";
import { usePrevious } from "@mantine/hooks";
import { CountUp } from "use-count-up";
import { copyPgn } from "./ContextMenu";
import { formatEvaluation, getOpponentLevelToDisplay } from "./utils";
import { LichessResponse } from "./getOpeningBookMove";

interface Props {
    game: Chess;
    onBack: () => void;
    resetPosition: () => void;
    shouldReset: ShouldReset;
    settings: Settings;
    state: State;
    mode: Mode;
    setState: (state: State) => void;
    level: Level;
    ratingGain: number | undefined;
    user: User | undefined;
    onRepeatLine: () => void;
    setMode: (mode: Mode) => void;
    lichessData?: LichessResponse;
    selectedPosition: Position | undefined;
}

const PlayingPanel: React.FC<Props> = ({
    game,
    onBack,
    resetPosition,
    shouldReset,
    settings,
    state,
    mode,
    setState,
    level,
    ratingGain,
    user,
    onRepeatLine,
    setMode,
    lichessData,
    selectedPosition,
}) => {
    const [isLoadingLichessLink, setIsLoadingLichessLink] = useState(false);
    const [stockfishModalOpened, { open: openStockfishModal, close: closeStockfishModal }] = useDisclosure(false);
    const playerColor = useStorage((state) => state.playerColor);
    const previousSelectedPosition = usePrevious(selectedPosition);
    const mistakesStorage = useMistakesStorage((state) => state);
    const uniquePositionAgainstPlayer = useStorage((state) => state.uniquePositionAgainstPlayer);
    const redoStack = useStorage((state) => state.redoStack);
    const stockfishEval = useStorage((state) => state.stockfishEval);

    const handleOnAnalyseOnLichess = async () => {
        const gameLink = await fetchLichessLink(game, playerColor, redoStack);

        // @ts-expect-error err
        window.open(gameLink).focus();
    };

    const currentMistakesIndex = mistakesStorage.currentIndex + (state === "attempt_finished" ? 1 : 0);

    const total = lichessData ? lichessData.white + lichessData.black + lichessData.draws : 0;

    return (
        <div className="h-full relative flex-col justify-between grid grid-rows-[1fr_7fr_1fr]">
            <div className="flex items-center justify-between">
                <Button onClick={onBack} leftSection={<IconArrowLeft />} variant="subtle">
                    Back
                </Button>
                {user?.isPremium && state === "attempt_finished" && (
                    <div className="space-x-2">
                        {/* <Tooltip label="Videos in this position">
                            <span className="relative">
                                <Badge size="xs" color="indigo" circle className="absolute  z-10 -top-4 right-0">
                                    3
                                </Badge>
                                <ActionIcon variant="subtle" opacity={0.9}>
                                    <IconVideo onClick={() => copyPgn(game)} />
                                </ActionIcon>
                            </span>
                        </Tooltip> */}
                        <Tooltip label="Copy PGN">
                            <ActionIcon variant="subtle" opacity={0.9}>
                                <IconCopy onClick={() => copyPgn(game)} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Repeat line (unrated)">
                            <ActionIcon variant="subtle" opacity={0.9}>
                                <IconTargetArrow onClick={onRepeatLine} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                )}
            </div>

            <div>
                {mode === "rated" && (
                    <>
                        <Text c="dimmed">Your rating</Text>
                        <div className="relative flex items-center justify-center space-x-1">
                            <Text fz="h2" mt="-0.4rem">
                                <CountUp
                                    key={`${previousSelectedPosition?.rating}-${selectedPosition?.rating}`}
                                    isCounting={true}
                                    start={(selectedPosition?.rating || 0) - (ratingGain || 0)}
                                    end={selectedPosition?.rating}
                                    duration={1}
                                />
                                {/* <CountUp end={selectedPosition?.rating || 1200} duration={1} separator="">
                                    {({ countUpRef }) => (
                                        <div>
                                            <span ref={countUpRef} />
                                        </div>
                                    )}
                                </CountUp> */}
                            </Text>
                            {state === "attempt_finished" && ratingGain !== undefined && (
                                <Text fw="bold" size="xl" c={ratingGain >= 0 ? "green" : "red"}>
                                    <sup>
                                        {ratingGain >= 0 ? "+" : ""}
                                        {ratingGain}
                                    </sup>
                                </Text>
                            )}
                        </div>
                        {selectedPosition?.engineSource === "lichessPlayer" && !uniquePositionAgainstPlayer && (
                            <Text fz="xs" c="dimmed">
                                opponent: {selectedPosition.lichessPlayer}
                            </Text>
                        )}
                        {selectedPosition?.engineSource === "lichessPlayer" &&
                            !uniquePositionAgainstPlayer &&
                            total > 0 && (
                                <Text fz="xs" c="dimmed">
                                    {total} games in current position
                                </Text>
                            )}
                        {(settings.engineSource === "lichess" || uniquePositionAgainstPlayer) && (
                            <Text fz="xs" c="dimmed">
                                opponent level: {getOpponentLevelToDisplay(level)}
                            </Text>
                        )}
                    </>
                )}
                {mode === "mistakes" && (
                    <>
                        <Text c="dimmed">Training weakest lines</Text>
                        <Progress
                            mt="sm"
                            color="green"
                            value={(100 * currentMistakesIndex) / mistakesStorage.mistakeAttempts.length}
                        />
                        <Text mt="xs" c="dimmed">
                            {currentMistakesIndex} / {mistakesStorage.mistakeAttempts.length}
                        </Text>
                    </>
                )}
                <HistoryTable game={game} settings={settings} state={state} mode={mode} />
                {settings.useLocalStockfish && false && (
                    <Flex gap="0" dir="column" direction="column" align="flex-start" mt="md">
                        <Group>
                            <Text c="dimmed" fz="xs">
                                Stockfish eval: {formatEvaluation(stockfishEval?.positionEvaluation)} (Depth:{" "}
                                {stockfishEval?.depth})
                            </Text>
                            {stockfishEval?.isComplete && <IconCheck size={14} />}
                        </Group>
                        <Text c="dimmed" fz="xs">
                            Time taken: {stockfishEval?.time} ms
                        </Text>
                        <Text c="dimmed" fz="xs">
                            NPS: <NumberFormatter thousandSeparator value={stockfishEval?.nps} />
                        </Text>
                    </Flex>
                )}
                {(mode === "practice" || state === "attempt_finished") && (
                    <>
                        <Button
                            onClick={handleOnAnalyseOnLichess}
                            color="gray"
                            className="mt-6 mb-1"
                            leftSection={<IconSearch />}
                            variant="outline"
                            loading={isLoadingLichessLink}
                            w="100%"
                        >
                            Analyze on Lichess
                        </Button>
                    </>
                )}
                {(mode === "practice" || state === "attempt_finished") && settings.displayStockfishInfo && (
                    <>
                        <Button
                            onClick={openStockfishModal}
                            color="indigo"
                            className="mb-1"
                            variant="transparent"
                            w="100%"
                        >
                            See stockfish info
                        </Button>
                        <StockfishInfoModal opened={stockfishModalOpened} close={closeStockfishModal} />
                    </>
                )}
            </div>

            {/* {shouldReset.reason === "novelty" && mode === "practice" && (
                <div className="text-red-300">No lichess data for current position, please reset.</div>
            )} */}
            {shouldReset.reason === "wrongMove" && (
                <div className="text-red-300">
                    Wrong move played. Please follow moves from the selected custom position
                </div>
            )}

            {shouldReset.reason === "wrongMove" && (
                <Button
                    onClick={() => {
                        setState("attempt_finished");
                        resetPosition();
                    }}
                    className={cn("!h-14", { "mt-6": !shouldReset.shouldReset })}
                    color={shouldReset.shouldReset ? "red" : "blue"}
                >
                    Reset
                </Button>
            )}
            {state === "attempt_finished" && (
                <Button
                    onClick={() => {
                        if (mode === "mistakes") {
                            if (currentMistakesIndex === mistakesStorage.mistakeAttempts.length) {
                                setState("menu");
                                setMode("rated");
                                mistakesStorage.setCurrentIndex(0);
                                return;
                            } else {
                                mistakesStorage.setCurrentIndex(currentMistakesIndex);
                            }
                        }

                        resetPosition();
                    }}
                    className={cn("!h-14")}
                    color="green"
                >
                    Next
                </Button>
            )}
        </div>
    );
};

export default PlayingPanel;
